import type { Bundle, BundleEntry, FhirResource } from 'fhir/r4';

/**
 * Narrowing helpers for walking a FHIR `Bundle` down to typed resources.
 *
 * `Bundle.entry` is `0..*` and `BundleEntry.resource` is `0..1`, so both are optional
 * and `resource` is the whole `FhirResource` union. A search that matched nothing
 * genuinely has no `entry`.
 *
 * `bundleEntries` substitutes an empty array for an absent `entry`; the resources
 * helpers additionally drop entries whose `resource` is absent or of another
 * `resourceType`. They filter through a type predicate rather than a cast, so the
 * runtime filter and the compile-time narrowing are the same statement: a mixed bundle
 * drops the stranger instead of handing a mistyped resource to `parseFHIRx`.
 */

/**
 * A bundle entry known to carry a resource of exactly `T`.
 *
 * This is what every helper below narrows to, and what a `useState` holding
 * search results should be declared as — `BundleEntry` alone leaves `resource`
 * optional and unioned.
 */
export type ResourceEntry<T extends FhirResource['resourceType']> = BundleEntry & {
  resource: Extract<FhirResource, { resourceType: T }>;
};

/** A `/Practitioner` search entry — the shape the practitioner pickers consume. */
export type PractitionerEntry = ResourceEntry<'Practitioner'>;

/** `Bundle.entry` with its `0..*` absence resolved to the empty array. */
export function bundleEntries(bundle: Bundle | undefined): BundleEntry[] {
  return bundle?.entry ?? [];
}

/**
 * True when the entry carries a resource of exactly `resourceType`.
 */
export function isEntryOf<T extends FhirResource['resourceType']>(
  entry: BundleEntry,
  resourceType: T
): entry is ResourceEntry<T> {
  return entry.resource?.resourceType === resourceType;
}

/**
 * A standalone `Array.prototype.filter` predicate for one `resourceType`.
 *
 * Use where the surrounding code must keep its own `entry` handling — e.g. where an
 * absent `entry` should still throw, so routing through `bundleEntriesOf` (which
 * substitutes `[]`) would change behaviour. Narrows the resource type only.
 */
export function isResourceEntry<T extends FhirResource['resourceType']>(
  resourceType: T
): (entry: BundleEntry) => entry is ResourceEntry<T> {
  return (
    entry: BundleEntry
  ): entry is ResourceEntry<T> =>
    isEntryOf(entry, resourceType);
}

/**
 * Type-level narrowing for a single-type search bundle. Emits nothing.
 *
 * Not `isResourceEntry`: that is a runtime `filter`, and dropping entries whose
 * `resource` is absent (`0..1` in FHIR R4) turns an upstream TypeError into a
 * silently empty result. Use `isResourceEntry` only where the filter is wanted.
 */
export function asResourceEntries<T extends FhirResource['resourceType']>(
  entries: BundleEntry[],
  _resourceType: T
): ResourceEntry<T>[];
export function asResourceEntries<T extends FhirResource['resourceType']>(
  entries: BundleEntry[] | undefined,
  _resourceType: T
): ResourceEntry<T>[] | undefined;
export function asResourceEntries<T extends FhirResource['resourceType']>(
  entries: BundleEntry[] | undefined,
  _resourceType: T
): ResourceEntry<T>[] | undefined {
  return entries as ResourceEntry<T>[] | undefined;
}

/**
 * Every resource in `bundle` of the given `resourceType`, typed.
 *
 * A search bundle for a single resource type contains only that type plus any
 * `include`d resources, so for this app's queries the filter removes nothing at
 * runtime. Returns `[]` for an absent bundle or absent `entry`, matching the
 * `data?.entry` guard that precedes every existing call site.
 */
export function bundleResources<T extends FhirResource['resourceType']>(
  bundle: Bundle | undefined,
  resourceType: T
): Extract<FhirResource, { resourceType: T }>[] {
  return bundleEntries(bundle)
    .filter((entry): entry is ResourceEntry<T> =>
      isEntryOf(entry, resourceType)
    )
    .map((entry) => entry.resource);
}

/**
 * Every entry in `bundle` whose resource is of the given `resourceType`, typed.
 *
 * Use when the caller needs the entry wrapper (for `fullUrl`, `search.mode`, or
 * to keep an existing `{ resource }` shape) rather than the bare resource.
 */
export function bundleEntriesOf<T extends FhirResource['resourceType']>(
  bundle: Bundle | undefined,
  resourceType: T
): ResourceEntry<T>[] {
  return bundleEntries(bundle).filter(
    (entry): entry is ResourceEntry<T> =>
      isEntryOf(entry, resourceType)
  );
}
